import { actions, overlays } from '@ecosystem/newron-design-system';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { AuthenticationClient } from '../../authentication/client';
import { error } from '../../logging';
import { getSharedToList } from '../utils';
import { Filter, FilterAccess } from '../types';

import { SharingConfig, UserToShare } from './types';

const { Button } = actions;
const { Modal } = overlays;

export interface DeleteFilterModalProps extends SharingConfig {
  isOpen: boolean;
  itemToDelete: Filter;
  onClose: () => void;
  onDelete: () => void;
}

export const DeleteFilterModal = ({
  isOpen,
  itemToDelete,
  getUsers,
  onClose,
  onDelete,
}: DeleteFilterModalProps): JSX.Element => {
  const currentUserId = AuthenticationClient.getInstance().getUser()?.userId;
  const handleClose = useCallback(() => onClose(), [onClose]);
  const handleDelete = useCallback(() => onDelete(), [onDelete]);
  const [usersMap, setUsersMap] = useState<Map<string, UserToShare>>(new Map());

  useEffect(() => {
    (async () => {
      try {
        const listOfUsers = await getUsers();
        setUsersMap(new Map(listOfUsers.map(u => [u.userId, u])));
      } catch (err) {
        error(err);
        setUsersMap(new Map());
      }
    })();
  }, [getUsers]);

  const confirmationText = useMemo(() => {
    const permissions = itemToDelete?.permissions ?? [];
    const isOwner = permissions.some(p => p.access === FilterAccess.Owner && p.user.id === currentUserId);
    if (isOwner) {
      const users = permissions
        .filter(p => p.access === FilterAccess.View && usersMap.has(p.user.id))
        .map(p => usersMap.get(p.user.id));

      if (users.length > 0) {
        return (
          <>
            <div>{`This filter is shared with ${users.length} other user(s).`}</div>
            <div className="pb-4">{`If you delete this filter now, ${getSharedToList(users)} will lose access to the filter.`}</div>
          </>
        );
      }
    }

    return null;
  }, [currentUserId, itemToDelete?.permissions, usersMap]);

  return (
    <Modal onClose={handleClose} isOpen={isOpen}>
      <Modal.Header>Delete filter</Modal.Header>
      <Modal.Body>
        <div data-test-id="delete-filter-confirmation-text">
          {confirmationText}
          <div>
            Are you sure you want to delete filter <span className="font-bold">{`'${itemToDelete?.name}'`}</span>?
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button testId="delete-filter-cancel" mode="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button testId="delete-filter-confirmation" onClick={handleDelete}>
          Delete filter
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
